import React, { ReactNode, useMemo } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import styled from 'styled-components/native';

import { memo } from '@webtrader/core/constants/react';
import dynamicSize from '@webtrader/core/dynamic-size';

import TabOptionSelector, { IBaseTabOption } from './Selector';

export type ITabOption<T> = IBaseTabOption<T>;

type TabSize = 'default' | 'small' | 'xsmall';

interface IProps<T> {
  size?: TabSize;
  value?: T;
  suffix?: ReactNode;
  zIndex?: number;
  options?: IBaseTabOption<T>[];
  spaceAware?: boolean;
  borderBottom?: boolean;
  underlineSelected?: boolean;
  enableReselect?: boolean;
  onChange?: (value: T) => void;
}

const Tabs = <T extends string>(props: IProps<T>) => {
  const {
    size = 'default',
    value,
    suffix,
    zIndex,
    options = [],
    spaceAware,
    borderBottom = false,
    underlineSelected = true,
    enableReselect = false,
    onChange
  } = props;

  const { height, fontSize } = useMemo(() => {
    switch (size) {
      case 'small':
        return {
          height: 38,
          fontSize: 13
        };
      case 'xsmall':
        return {
          height: 36,
          fontSize: 12
        };
      default:
        return {
          height: 32,
          fontSize: 14
        };
    }
  }, [size]);

  const tabStyles = useMemo(() => {
    const styles: StyleProp<ViewStyle> = {
      height
    };

    if (zIndex) {
      styles.zIndex = zIndex;
    }

    return styles;
  }, [height, zIndex]);

  return (
    <TabOptions height={height} style={tabStyles} spaceAware={spaceAware} borderBottom={borderBottom}>
      <ScrollableOptions
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollableOptions}
      >
        {options.map((option) => {
          const selected = option.value === value;

          return (
            <TabOptionSelector
              key={option.value}
              option={option}
              fontSize={fontSize}
              selected={selected}
              underlineSelected={underlineSelected}
              enableReselect={enableReselect}
              onChange={onChange}
            />
          );
        })}
      </ScrollableOptions>
      {suffix && <TabOptionsSuffix>{suffix}</TabOptionsSuffix>}
    </TabOptions>
  );
};

export default memo(Tabs);

const styles = StyleSheet.create({
  scrollableOptions: {
    flexGrow: 1
  }
});

const ScrollableOptions = styled.ScrollView``;

interface ITabOptionsProps {
  height: number;
  zIndex?: number;
  spaceAware?: boolean;
  borderBottom?: boolean;
}

export const TabOptions = styled(View)<ITabOptionsProps>`
  height: ${({ height }) => `${dynamicSize.convertSize(height)}px`};

  flex-direction: row;

  padding-start: ${({ theme, spaceAware }) => (spaceAware ? theme.sizes.mobile.basePaddingStart : 0)}px;
  padding-end: ${({ theme, spaceAware }) => (spaceAware ? theme.sizes.mobile.basePaddingEnd : 0)}px;

  border-bottom-width: ${({ borderBottom }) => (borderBottom ? 1 : 0)}px;
  border-bottom-color: ${({ theme }) => theme.colors.border[0]};
`;

const TabOptionsSuffix = styled.View`
  align-items: center;
  flex-direction: row;
`;
